import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2, FileText, CreditCard, Truck, MapPin, ChevronLeft, Store, Check, Tag, X } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice, fetchAvailableCountries, ShopifyCountry } from "@/lib/shopify";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { calculateShipping, getTotalCartWeight, ShippingCalculation } from "@/lib/shippingCalculator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getLocalizedTitle, type Language } from "@/lib/productTranslations";
import { trackViewCart, trackBeginCheckout, trackRemoveFromCart } from "@/lib/analytics";

interface DeliveryAddress {
  address: string;
  city: string;
  postal_code: string;
  country: string;
}

export const CartDrawer = () => {
  const { t, language } = useLanguage();
  const { businessCustomer, isApprovedBusinessCustomer } = useAuth();
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [shippingInfo, setShippingInfo] = useState<ShippingCalculation | null>(null);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [showDeliveryStep, setShowDeliveryStep] = useState(false);
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<ShopifyCountry[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<'delivery' | 'pickup'>('delivery');
  const [orderNotes, setOrderNotes] = useState('');
  
  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    discountAmount: number;
  } | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>({
    address: '',
    city: '',
    postal_code: '',
    country: 'Latvia',
  });
  
  // Store pickup address
  const STORE_PICKUP_ADDRESS: DeliveryAddress = {
    address: 'Cēsu iela 31/K3',
    city: 'Rīga',
    postal_code: 'LV-1012',
    country: 'Latvia',
  };
  
  const { 
    items, 
    isLoading, 
    isOpen,
    updateQuantity, 
    removeItem, 
    createCheckout,
    setOpen,
    clearCart,
    getTotalItems,
    getTotalPrice,
  } = useCartStore();
  
  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();
  const currencyCode = items[0]?.price.currencyCode || 'EUR';
  
  // Calculate total weight
  const totalWeight = getTotalCartWeight(items);
  
  // Check if any cart item has a "Sale" tag
  const hasSaleItems = items.some(item => 
    item.product.node.tags?.some((t: string) => t.toLowerCase() === 'sale')
  );

  // Determine per-item sale status: variant has compareAtPrice > price, OR product is tagged 'sale'.
  // Used to exclude sale items from B2B percentage discount (no stacking).
  const isItemOnSale = (item: typeof items[number]): boolean => {
    const variants = item.product.node.variants?.edges || [];
    const variant = variants.find(v => v.node.id === item.variantId)?.node;
    if (variant?.compareAtPrice) {
      const cmp = parseFloat(variant.compareAtPrice.amount);
      const cur = parseFloat(variant.price.amount);
      if (!isNaN(cmp) && !isNaN(cur) && cmp > cur) return true;
    }
    return item.product.node.tags?.some((t: string) => t.toLowerCase() === 'sale') || false;
  };

  // Subtotal eligible for the B2B percentage discount (excludes sale items).
  const discountableSubtotal = items.reduce((sum, item) => {
    if (isItemOnSale(item)) return sum;
    return sum + parseFloat(item.price.amount) * item.quantity;
  }, 0);
  
  // Get saved delivery address
  const savedDeliveryAddress: DeliveryAddress | null = businessCustomer ? {
    address: businessCustomer.delivery_address || businessCustomer.billing_address || '',
    city: businessCustomer.delivery_city || businessCustomer.city || '',
    postal_code: businessCustomer.delivery_postal_code || businessCustomer.postal_code || '',
    country: businessCustomer.delivery_country || businessCustomer.country || 'Latvia',
  } : null;
  
  // Determine which address to use for shipping calculation
  const activeDeliveryAddress = useNewAddress ? deliveryAddress : savedDeliveryAddress;
  
  // Initialize delivery address from saved when opening
  useEffect(() => {
    if (isOpen && savedDeliveryAddress) {
      setDeliveryAddress(savedDeliveryAddress);
    }
  }, [isOpen, businessCustomer]);

  // GA4: view_cart when drawer opens with items
  useEffect(() => {
    if (isOpen && items.length > 0) {
      trackViewCart(items, totalPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch available countries from Shopify when cart opens (for business customer address selector)
  useEffect(() => {
    const loadCountries = async () => {
      if (!isOpen) return;
      
      setIsLoadingCountries(true);
      const countries = await fetchAvailableCountries();
      setAvailableCountries(countries);
      setIsLoadingCountries(false);
    };
    
    loadCountries();
  }, [isOpen]);
  
  // Calculate shipping when cart changes or delivery address changes (business customers only)
  useEffect(() => {
    const fetchShipping = async () => {
      if (items.length === 0 || !isApprovedBusinessCustomer) {
        setShippingInfo(null);
        return;
      }
      
      setIsCalculatingShipping(true);
      
      // For business customers, use database rates
      const country = activeDeliveryAddress?.country || 'Latvia';
      const shipping = await calculateShipping(totalWeight, country, totalPrice);
      setShippingInfo(shipping);
      setIsCalculatingShipping(false);
    };
    
    fetchShipping();
  }, [items, totalWeight, totalPrice, activeDeliveryAddress?.country, isApprovedBusinessCustomer]);
  
  // Calculate business customer pricing
  // Note: VAT is already included in product prices
  // B2B % discount does NOT stack with Sale prices — applies only to non-sale items.
  const discountPercentage = businessCustomer?.discount_percentage || 0;
  const discountAmount = discountableSubtotal * (discountPercentage / 100);
  const afterDiscount = totalPrice - discountAmount;
  // Promo code discount (applied after business discount)
  const promoDiscount = appliedPromo?.discountAmount || 0;
  const afterPromo = Math.max(0, afterDiscount - promoDiscount);
  // Free shipping for store pickup
  const shippingCost = deliveryMethod === 'pickup' ? 0 : (shippingInfo?.shippingCost || 0);
  const finalTotal = afterPromo + shippingCost;

  // For regular customers, promo applies to totalPrice
  const regularPromoDiscount = appliedPromo?.discountAmount || 0;
  const regularAfterPromo = Math.max(0, totalPrice - regularPromoDiscount);

  // Validate promo code
  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    
    setIsValidatingPromo(true);
    try {
      const response = await supabase.functions.invoke('manage-promo-codes', {
        body: { action: 'validate', code, orderTotal: totalPrice }
      });
      
      if (response.error) throw response.error;
      const data = response.data;
      
      if (data.valid) {
        setAppliedPromo({
          code: data.code,
          discountType: data.discountType,
          discountValue: data.discountValue,
          discountAmount: data.discountAmount,
        });
        setPromoInput('');
        toast.success(t.cart.promoApplied || 'Promo code applied!');
      } else {
        toast.error(data.error || t.cart.promoInvalid || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Promo validation error:', error);
      toast.error(t.cart.promoInvalid || 'Invalid promo code');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  // Auto-remove promo if sale items are added to cart
  useEffect(() => {
    if (hasSaleItems && appliedPromo) {
      setAppliedPromo(null);
      setPromoInput('');
      toast.info(t.cart.promoNotAvailableWithSale);
    }
  }, [hasSaleItems]);

  // Re-validate promo when cart total changes
  useEffect(() => {
    if (appliedPromo && totalPrice > 0 && !hasSaleItems) {
      const revalidate = async () => {
        try {
          const response = await supabase.functions.invoke('manage-promo-codes', {
            body: { action: 'validate', code: appliedPromo.code, orderTotal: totalPrice }
          });
          if (response.data?.valid) {
            setAppliedPromo(prev => prev ? { ...prev, discountAmount: response.data.discountAmount } : null);
          } else {
            setAppliedPromo(null);
          }
        } catch { /* ignore */ }
      };
      revalidate();
    }
  }, [totalPrice]);

  const handleShopifyCheckout = async () => {
    trackBeginCheckout(items, totalPrice);
    // Open window immediately on user click to avoid Safari popup blocker
    const checkoutWindow = window.open('about:blank', '_blank');
    
    try {
      const checkoutUrl = await createCheckout(appliedPromo?.code);
      if (checkoutUrl) {
        if (checkoutWindow && !checkoutWindow.closed) {
          checkoutWindow.location.href = checkoutUrl;
        } else {
          // Fallback: navigate current tab if popup was blocked
          window.location.href = checkoutUrl;
        }
        clearCart();
        setOpen(false);
      } else {
        checkoutWindow?.close();
        toast.error(t.common.error, {
          description: 'Failed to create checkout URL',
        });
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      checkoutWindow?.close();
      toast.error(t.common.error, {
        description: t.common.error,
      });
    }
  };

  const handleBusinessCheckout = async () => {
    if (!businessCustomer) return;
    trackBeginCheckout(items, finalTotal);
    // Use store address for pickup, otherwise use delivery address
    const checkoutAddress = deliveryMethod === 'pickup' ? STORE_PICKUP_ADDRESS : activeDeliveryAddress;
    
    // Validate delivery address (only for delivery, not pickup)
    if (deliveryMethod === 'delivery' && (!checkoutAddress?.address || !checkoutAddress?.city || 
        !checkoutAddress?.postal_code || !checkoutAddress?.country)) {
      toast.error(t.cart.fillDeliveryAddress);
      return;
    }
    
    setIsCreatingInvoice(true);
    try {
      const orderItems = items.map(item => ({
        productId: item.product.node.id,
        variantId: item.variantId,
        title: item.product.node.title,
        variantTitle: item.variantTitle,
        quantity: item.quantity,
        price: parseFloat(item.price.amount),
        sku: item.product.node.variants?.edges?.[0]?.node?.id || '',
        weight: item.weight || 0,
      }));

      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: {
          customerId: businessCustomer.id,
          items: orderItems,
          subtotal: totalPrice,
          discountPercentage: discountPercentage,
          discountableSubtotal: discountableSubtotal,
          vatRate: 21, // VAT is already included in prices, this is for invoice display only
          shippingAmount: shippingCost,
          totalWeight: totalWeight,
          deliveryAddress: deliveryMethod === 'pickup' ? STORE_PICKUP_ADDRESS : activeDeliveryAddress,
          deliveryMethod: deliveryMethod,
          notes: orderNotes || undefined,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(t.cart.orderPlaced, {
          description: t.cart.orderPlacedDesc,
        });
        
        // Open invoice in new window
        if (data.invoiceHtml) {
          const invoiceWindow = window.open('', '_blank');
          if (invoiceWindow) {
            invoiceWindow.document.write(data.invoiceHtml);
            invoiceWindow.document.close();
          }
        }
        
        clearCart();
        setShowDeliveryStep(false);
        setOpen(false);
      }
    } catch (error) {
      console.error('Invoice creation failed:', error);
      toast.error(t.common.error, {
        description: 'Failed to create invoice',
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };
  
  const handleProceedToDelivery = () => {
    setShowDeliveryStep(true);
  };
  
  const handleBackToCart = () => {
    setShowDeliveryStep(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full bg-background border-l border-border">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-2xl text-foreground">{t.cart.title}</SheetTitle>
          <SheetDescription className="text-muted-foreground font-body">
            {totalItems === 0 
              ? t.cart.empty 
              : `${totalItems} ${totalItems === 1 ? t.shop.product : t.shop.products}`
            }
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
                  <ShoppingBag className="h-10 w-10 text-muted-foreground" />
                </div>
                <p className="text-foreground font-semibold mb-2 font-display">{t.cart.empty}</p>
                <p className="text-sm text-muted-foreground font-body">
                  {t.cart.emptyDesc}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-2 sm:space-y-4">
                  {items.map((item) => (
                    <div 
                      key={item.variantId} 
                      className="flex gap-2.5 sm:gap-4 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-card border border-border animate-fade-in"
                    >
                      <div className="w-14 h-14 sm:w-20 sm:h-20 bg-secondary rounded-lg sm:rounded-xl overflow-hidden flex-shrink-0">
                        {(item.variantImage?.url || item.product.node.images?.edges?.[0]?.node?.url) && (
                          <img
                            src={item.variantImage?.url || item.product.node.images.edges[0].node.url}
                            alt={item.variantImage?.altText || item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-xs sm:text-sm line-clamp-2 text-foreground font-display">
                          {getLocalizedTitle(item.product.node, language as Language)}
                        </h4>
                        {item.variantTitle !== 'Default Title' && (
                          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 font-body line-clamp-1">
                            {item.selectedOptions.map(option => option.value).join(' • ')}
                          </p>
                        )}
                        <p className="font-display font-bold text-copper text-sm sm:text-base mt-1 sm:mt-2">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-1.5 sm:gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 sm:h-7 sm:w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => { trackRemoveFromCart(item); removeItem(item.variantId); }}
                        >
                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </Button>
                        
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6 sm:h-7 sm:w-7"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 sm:w-8 text-center text-xs sm:text-sm font-semibold text-foreground">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6 sm:h-7 sm:w-7"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Checkout section - scrollable when delivery step shown */}
              <div className={`flex-shrink-0 pt-3 sm:pt-6 border-t border-border bg-background ${showDeliveryStep ? 'overflow-y-auto max-h-[50vh]' : ''}`}>
                <div className="space-y-2.5 sm:space-y-4">
                  {/* Pricing summary */}
                  <div className="space-y-1 sm:space-y-2">
                    {/* For regular customers - show VAT breakdown, shipping calculated at checkout */}
                    {!isApprovedBusinessCustomer && (
                      <>
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                          <span className="text-muted-foreground">{t.cart.subtotalExVat}</span>
                          <span className="text-foreground">
                            {formatPrice((regularAfterPromo / 1.21).toFixed(2), currencyCode)}
                          </span>
                        </div>
                        {appliedPromo && (
                          <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-green-600 flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {appliedPromo.code} ({appliedPromo.discountType === 'percentage' ? `${appliedPromo.discountValue}%` : `€${appliedPromo.discountValue}`})
                            </span>
                            <span className="text-green-600">
                              -{formatPrice(regularPromoDiscount.toFixed(2), currencyCode)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                          <span className="text-muted-foreground">{t.cart.vatAmount}</span>
                          <span className="text-foreground">
                            {formatPrice((regularAfterPromo - regularAfterPromo / 1.21).toFixed(2), currencyCode)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Truck className="h-3 w-3" />
                            {t.cart.shipping}
                          </span>
                          <span className="text-muted-foreground text-[10px] sm:text-xs">
                            {t.cart.calculatedAtCheckout}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-1.5 sm:pt-2 border-t border-border">
                          <span className="text-sm sm:text-lg font-semibold text-foreground font-display">
                            {t.cart.total}
                          </span>
                          <span className="text-lg sm:text-2xl font-display font-bold text-foreground">
                            {formatPrice(regularAfterPromo.toFixed(2), currencyCode)}
                          </span>
                        </div>
                      </>
                    )}
                    
                    {/* For business customers - show VAT breakdown */}
                    {isApprovedBusinessCustomer && (
                      <>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">{t.cart.subtotalExVat}</span>
                          <span className="text-foreground">
                            {formatPrice((afterPromo / 1.21).toFixed(2), currencyCode)}
                          </span>
                        </div>
                        
                        {discountPercentage > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-copper">
                              {t.cart.yourDiscount} ({discountPercentage}%)
                              {hasSaleItems && (
                                <span className="block text-xs text-muted-foreground">
                                  {language === 'lv' && 'Netiek piemērota akcijas precēm'}
                                  {language === 'en' && 'Not applied to sale items'}
                                  {language === 'ru' && 'Не применяется к товарам со скидкой'}
                                </span>
                              )}
                            </span>
                            <span className="text-copper">
                              -{formatPrice(discountAmount.toString(), currencyCode)}
                            </span>
                          </div>
                        )}

                        {appliedPromo && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-green-600 flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {appliedPromo.code} ({appliedPromo.discountType === 'percentage' ? `${appliedPromo.discountValue}%` : `€${appliedPromo.discountValue}`})
                            </span>
                            <span className="text-green-600">
                              -{formatPrice(promoDiscount.toFixed(2), currencyCode)}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">{t.cart.vatAmount}</span>
                          <span className="text-foreground">
                            {formatPrice((afterPromo - afterPromo / 1.21).toFixed(2), currencyCode)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center pt-2 border-t border-border">
                          <span className="text-lg font-semibold text-foreground font-display">
                            {t.cart.total}
                          </span>
                          <span className="text-2xl font-display font-bold text-foreground">
                            {formatPrice(finalTotal.toString(), currencyCode)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Promo Code Input */}
                  <div className="space-y-2">
                    {hasSaleItems ? (
                      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-muted border border-border">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {t.cart.promoNotAvailableWithSale}
                        </span>
                      </div>
                    ) : appliedPromo ? (
                      <div className="flex items-center justify-between p-2.5 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-400">
                            {appliedPromo.code}
                          </span>
                          <span className="text-xs text-green-600 dark:text-green-500">
                            -{appliedPromo.discountType === 'percentage' ? `${appliedPromo.discountValue}%` : `€${appliedPromo.discountValue}`}
                          </span>
                        </div>
                        <button
                          onClick={handleRemovePromo}
                          className="text-green-600 hover:text-destructive transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 pb-0.5 sm:pb-1">
                        <Input
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          placeholder={t.cart.promoPlaceholder}
                          className="h-8 sm:h-9 font-mono text-xs sm:text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleApplyPromo}
                          disabled={!promoInput.trim() || isValidatingPromo}
                          className="h-9 px-4 whitespace-nowrap"
                        >
                          {isValidatingPromo ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            t.cart.applyPromo
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  
                  {/* Delivery Address Step for Business Customers */}
                  {isApprovedBusinessCustomer && showDeliveryStep && (
                    <div className="space-y-3 p-3 rounded-xl bg-card border border-border">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-copper" />
                        <h4 className="font-semibold text-foreground text-sm">{t.cart.deliveryAddress}</h4>
                      </div>
                      
                      {/* Delivery method selection */}
                      <div className="space-y-2 mb-3">
                        <p className="text-xs text-muted-foreground font-medium">{t.cart.deliveryMethod}</p>
                        
                        {/* Store pickup option */}
                        <button
                          type="button"
                          onClick={() => setDeliveryMethod('pickup')}
                          className={`w-full p-3 rounded-lg border text-left transition-all ${
                            deliveryMethod === 'pickup' 
                              ? 'border-copper bg-copper/10' 
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                              deliveryMethod === 'pickup' ? 'border-copper bg-copper' : 'border-muted-foreground'
                            }`}>
                              {deliveryMethod === 'pickup' && <Check className="h-2.5 w-2.5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Store className="h-4 w-4 text-copper" />
                                <p className="text-sm font-medium text-foreground">{t.cart.pickupInStore}</p>
                                <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded">{t.cart.free}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {STORE_PICKUP_ADDRESS.address}, {STORE_PICKUP_ADDRESS.city} {STORE_PICKUP_ADDRESS.postal_code}, {STORE_PICKUP_ADDRESS.country}
                              </p>
                            </div>
                          </div>
                        </button>
                        
                        {/* Delivery option */}
                        <button
                          type="button"
                          onClick={() => setDeliveryMethod('delivery')}
                          className={`w-full p-3 rounded-lg border text-left transition-all ${
                            deliveryMethod === 'delivery' 
                              ? 'border-copper bg-copper/10' 
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                              deliveryMethod === 'delivery' ? 'border-copper bg-copper' : 'border-muted-foreground'
                            }`}>
                              {deliveryMethod === 'delivery' && <Check className="h-2.5 w-2.5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Truck className="h-4 w-4 text-copper" />
                                <p className="text-sm font-medium text-foreground">{t.cart.deliveryToAddress}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {t.cart.deliveryPriceNote}
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                      
                      {/* Address selection - only show when delivery is selected */}
                      {deliveryMethod === 'delivery' && (
                        <>
                          {savedDeliveryAddress && savedDeliveryAddress.address && (
                            <div className="space-y-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setUseNewAddress(false);
                                  setDeliveryAddress(savedDeliveryAddress);
                                }}
                                className={`w-full p-2 rounded-lg border text-left transition-all ${
                                  !useNewAddress 
                                    ? 'border-copper bg-copper/10' 
                                    : 'border-border hover:border-muted-foreground'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                                    !useNewAddress ? 'border-copper bg-copper' : 'border-muted-foreground'
                                  }`}>
                                    {!useNewAddress && <Check className="h-2.5 w-2.5 text-white" />}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{t.cart.savedAddress}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                      {savedDeliveryAddress.address}, {savedDeliveryAddress.city}
                                    </p>
                                  </div>
                                </div>
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => setUseNewAddress(true)}
                                className={`w-full p-2 rounded-lg border text-left transition-all ${
                                  useNewAddress 
                                    ? 'border-copper bg-copper/10' 
                                    : 'border-border hover:border-muted-foreground'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                                    useNewAddress ? 'border-copper bg-copper' : 'border-muted-foreground'
                                  }`}>
                                    {useNewAddress && <Check className="h-2.5 w-2.5 text-white" />}
                                  </div>
                                  <p className="text-sm font-medium text-foreground">{t.cart.newAddress}</p>
                                </div>
                              </button>
                            </div>
                          )}
                          
                          {/* New address form - compact */}
                          {(useNewAddress || !savedDeliveryAddress?.address) && (
                            <div className="space-y-2">
                              <div>
                                <Label htmlFor="delivery-country" className="text-xs text-muted-foreground">{t.cart.country}</Label>
                                <Select
                                  value={deliveryAddress.country}
                                  onValueChange={(value) => setDeliveryAddress(prev => ({ ...prev, country: value }))}
                                >
                                  <SelectTrigger id="delivery-country" className="mt-1 h-9">
                                    <SelectValue placeholder={t.cart.selectCountry} />
                                  </SelectTrigger>
                                  <SelectContent className="bg-popover z-50">
                                    {availableCountries.map((country) => (
                                      <SelectItem key={country.isoCode} value={country.name}>
                                        {country.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div>
                                <Label htmlFor="delivery-address" className="text-xs text-muted-foreground">{t.cart.address}</Label>
                                <Input
                                  id="delivery-address"
                                  value={deliveryAddress.address}
                                  onChange={(e) => setDeliveryAddress(prev => ({ ...prev, address: e.target.value }))}
                                  placeholder={t.cart.streetAddress}
                                  className="mt-1 h-9"
                                />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label htmlFor="delivery-city" className="text-xs text-muted-foreground">{t.cart.city}</Label>
                                  <Input
                                    id="delivery-city"
                                    value={deliveryAddress.city}
                                    onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
                                    placeholder={t.cart.city}
                                    className="mt-1 h-9"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="delivery-postal" className="text-xs text-muted-foreground">{t.cart.postalCode}</Label>
                                  <Input
                                    id="delivery-postal"
                                    value={deliveryAddress.postal_code}
                                    onChange={(e) => setDeliveryAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                    placeholder="LV-1234"
                                    className="mt-1 h-9"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Order Notes */}
                      <div className="pt-2">
                        <Label htmlFor="order-notes" className="text-xs text-muted-foreground">{t.cart.orderNotes}</Label>
                        <Textarea
                          id="order-notes"
                          value={orderNotes}
                          onChange={(e) => setOrderNotes(e.target.value)}
                          placeholder={t.cart.orderNotesPlaceholder}
                          className="mt-1 min-h-[60px] resize-none"
                          rows={2}
                        />
                      </div>
                      
                      {/* Updated shipping info based on selected method */}
                      <div className="pt-2 border-t border-border">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground flex items-center gap-1">
                            {deliveryMethod === 'pickup' ? (
                              <>
                                <Store className="h-3 w-3" />
                                {t.cart.storePickup}
                              </>
                            ) : (
                              <>
                                <Truck className="h-3 w-3" />
                                {t.cart.shipping} ({totalWeight.toFixed(1)} kg)
                              </>
                            )}
                          </span>
                          {deliveryMethod === 'pickup' ? (
                            <span className="text-green-500 font-medium">{t.cart.freeShipping}</span>
                          ) : isCalculatingShipping ? (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                          ) : shippingInfo?.isFreeShipping ? (
                            <span className="text-green-500 font-medium">{t.cart.freeShipping}</span>
                          ) : (
                            <span className="text-foreground font-medium">
                              {formatPrice(shippingCost.toString(), currencyCode)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                
              {/* Fixed checkout buttons - always visible at bottom */}
              <div className="flex-shrink-0 space-y-3 pt-4 bg-background">
                {isApprovedBusinessCustomer ? (
                  showDeliveryStep ? (
                    <>
                      {/* Confirm order button */}
                      <Button 
                        variant="hero"
                        onClick={handleBusinessCheckout}
                        className="w-full" 
                        size="xl"
                        disabled={items.length === 0 || isCreatingInvoice}
                      >
                        {isCreatingInvoice ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {t.common.loading}
                          </>
                        ) : (
                          <>
                            <FileText className="w-5 h-5 mr-2" />
                            {t.cart.confirmOrder}
                          </>
                        )}
                      </Button>
                      
                      {/* Back button */}
                      <Button 
                        variant="outline"
                        onClick={handleBackToCart}
                        className="w-full" 
                        size="lg"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        {t.cart.backToCart}
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Proceed to delivery step */}
                      <Button 
                        variant="hero"
                        onClick={handleProceedToDelivery}
                        className="w-full" 
                        size="xl"
                        disabled={items.length === 0}
                      >
                        <MapPin className="w-5 h-5 mr-2" />
                        {t.cart.proceedToDelivery}
                      </Button>
                    </>
                  )
                ) : (
                  /* Regular checkout - terms checkbox + Shopify button */
                  <>
                    <div className="flex items-start gap-2 py-2">
                      <Checkbox
                        id="terms-agree"
                        checked={termsAccepted}
                        onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                        className="mt-0.5"
                      />
                      <label htmlFor="terms-agree" className="text-xs sm:text-sm text-muted-foreground font-body leading-tight cursor-pointer">
                        {t.cart.agreeToTerms}{' '}
                        <Link
                          to={`/${language}/purchase-terms`}
                          target="_blank"
                          className="text-copper underline hover:text-foreground transition-colors"
                        >
                          {t.cart.purchaseTermsLink}
                        </Link>
                      </label>
                    </div>
                    <Button 
                      variant="hero"
                      onClick={handleShopifyCheckout}
                      className="w-full" 
                      size="xl"
                      disabled={items.length === 0 || isLoading || !termsAccepted}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                          {t.common.loading}
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          {t.cart.checkout}
                        </>
                      )}
                    </Button>
                  </>
                )}

                <p className="text-[10px] sm:text-xs text-center text-muted-foreground font-body">
                  {isApprovedBusinessCustomer 
                    ? t.cart.invoiceWillBeSent
                    : t.cart.secureCheckout
                  }
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
